import { useEffect } from 'react';
import * as stores from '../stores';

/**
 * Global hook that triggers paywall every 2 app opens for non-subscribers.
 * Should be used once at the app level to ensure consistent behavior.
 *
 * **Race Condition Prevention:**
 * Waits for `isPurchaseInfoLoaded = true` before checking subscription status.
 * This ensures we use fresh data from RevenueCat, not stale cached data.
 *
 * **Behavior:**
 * - Shows paywall on 2nd, 4th, 6th, etc. app opens
 * - Only for non-subscribers who haven't seen it this session
 * - On iOS: Waits for RevenueCat API to return fresh subscription status
 * - On web: purchaserInfo is undefined (treated as non-subscriber)
 */
export const useGlobalPaywallTrigger = () => {
  const isPurchaseInfoLoaded = stores.Purchase.useState((s) => s.isPurchaseInfoLoaded);
  const purchaserInfo = stores.Purchase.useState((s) => s.purchaserInfo);
  const isSubscribed = purchaserInfo?.entitlements?.active[stores.entitlementIdentifier]?.isActive;
  const appOpenDates = stores.General.useState((s) => s.appOpenDates);
  const hasSeenTakeover = stores.General.useState((s) => s.hasSeenSubscriptionTakeoverThisSession);

  useEffect(() => {
    // Wait for fresh purchase info to load from RevenueCat before checking subscription status
    // This prevents showing paywall to subscribed users due to stale cached data
    if (!isPurchaseInfoLoaded) {
      return;
    }

    // Only trigger for non-subscribers who haven't seen paywall this session
    if (!isSubscribed && !hasSeenTakeover && appOpenDates && appOpenDates.length % 2 === 0) {
      stores.General.update((s) => {
        s.hasSeenSubscriptionTakeoverThisSession = true;
      });
      stores.showPaymentModalByPreference('app_open_interval');
    }
  }, [appOpenDates, isSubscribed, hasSeenTakeover, isPurchaseInfoLoaded]);
};
